import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import axios from 'axios'
import ParentTableRow from './ParentTableRow'
import AddParent from './AddParent'
import EditParent from './EditParent'
import AddStudent from '../Student/AddStudent'
import {
  AddIcon,
  SmallArrowDownIcon,
  SearchIcon,
  EditIcon,
  DeleteIcon,
} from '../Icons'
import StatusToggle from '../ui/StatusToggle'
import ConfirmModal from '../ui/ConfirmModal'

// Skeleton loading component
const TableSkeleton = () => (
  <div className="animate-pulse">
    <table className="w-full">
      <thead className="bg-neutral-50">
        <tr>
          <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0]">
            S.No
          </th>
          <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
            Parent Name
          </th>
          <th className="text-sm text-[#535862] font-medium text-left border w-[280px] px-5 py-3.5 border-[#F0F0F0]">
            Email Address
          </th>
          <th className="text-sm text-[#535862] font-medium text-center border px-5 py-3.5 border-[#F0F0F0]">
            Status
          </th>
          <th className="text-sm text-[#535862] font-medium text-center px-5 py-3.5 border border-[#F0F0F0]">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {[...Array(5)].map((_, index) => (
          <tr key={index} className="border border-[#F0F0F0]">
            <td className="text-center border px-5 py-2 border-[#F0F0F0]">
              <div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
            </td>
            <td className="border px-5 py-2 border-[#F0F0F0]">
              <div className="h-4 bg-gray-200 rounded w-32"></div>
            </td>
            <td className="border px-5 py-2 border-[#F0F0F0]">
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </td>
            <td className="text-center border px-5 py-2 border-[#F0F0F0]">
              <div className="h-6 bg-gray-200 rounded w-16 mx-auto"></div>
            </td>
            <td className="px-5 py-2">
              <div className="flex items-center justify-center gap-1.5">
                <div className="h-8 w-8 bg-gray-200 rounded-[10px]"></div>
                <div className="h-8 w-8 bg-gray-200 rounded-[10px]"></div>
                <div className="h-8 w-8 bg-gray-200 rounded-[10px]"></div>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

function ParentList() {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAddParentModal, setShowAddParentModal] = useState(false)
  const [showEditParentModal, setShowEditParentModal] = useState(false)
  const [showAddStudentModal, setShowAddStudentModal] = useState(false)
  const [selectedParentId, setSelectedParentId] = useState(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalAction, setStatusModalAction] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [parentToDelete, setParentToDelete] = useState(null)
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [entriesPerPage, setEntriesPerPage] = useState('100')
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getParents()
      } catch (error) {
        console.error('Error loading data:', error)
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const deleteParent = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}delete_user/${id}`
      )
      toast.success(response.data)
      getParents()
    } catch (error) {
      toast.error('Failed to delete parent')
    }
  }

  const blockParent = async (id) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}block_user/${id}`
      )
      toast.success(response.data)
      getParents()
    } catch (error) {
      toast.error('Failed to deactivate parent')
    }
  }

  const unblockParent = async (id) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}unblock_user/${id}`
      )
      toast.success(response.data)
      getParents()
    } catch (error) {
      toast.error('Failed to activate parent')
    }
  }

  const getParents = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}getallparents`
    )
    if (response.status === 200) {
      setData(response.data)
      setIsLoading(false)
    }
  }

  const handleStatusToggle = (id, currentStatus) => {
    setStatusModalAction({ id, type: currentStatus ? 'block' : 'unblock' })
    setStatusModalOpen(true)
  }

  const handleStatusModalConfirm = async () => {
    if (statusModalAction) {
      if (statusModalAction.type === 'block') {
        await blockParent(statusModalAction.id)
      } else {
        await unblockParent(statusModalAction.id)
      }
    }
    setStatusModalOpen(false)
    setStatusModalAction(null)
  }

  const handleStatusModalCancel = () => {
    setStatusModalOpen(false)
    setStatusModalAction(null)
  }

  const handleDeleteClick = (id) => {
    setParentToDelete(id)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (parentToDelete) {
      await deleteParent(parentToDelete)
    }
    setDeleteModalOpen(false)
    setParentToDelete(null)
  }

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false)
    setParentToDelete(null)
  }

  // Filter data based on search term
  const filteredData = data.filter(
    (item) =>
      item.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Add pagination logic using filteredData
  const indexOfLastEntry = currentPage * parseInt(entriesPerPage)
  const indexOfFirstEntry = indexOfLastEntry - parseInt(entriesPerPage)
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry)
  const totalPages = Math.ceil(filteredData.length / parseInt(entriesPerPage))

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
  }

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0]">
      {/* Header Controls Section - Reference Project Styles */}
      <div className="justify-between items-center border border-[#F0F0F0] flex w-full gap-[40px_100px] font-medium leading-[1.2] flex-wrap bg-white px-5 py-4 rounded-t-[10px]">
        <h2 className="text-lg text-[#030303] font-bold">Parent List</h2>
        <div className="self-stretch flex min-w-60 items-center gap-2.5 text-xs text-[#535862] flex-wrap w-[750px] my-auto max-md:max-w-full">
          <div className="self-stretch flex items-center gap-[9px] my-auto">
            <div className="text-[#535862] self-stretch my-auto">
              Show Entries
            </div>
            <div className="self-stretch flex items-center gap-2 whitespace-nowrap text-center w-[90px] my-auto relative">
              <div className="justify-center items-stretch border border-[#F0F0F0] self-stretch flex w-[90px] flex-col bg-white my-auto px-2.5 py-[9px] rounded-[10px] border-solid relative">
                <select
                  className="text-[#535862] self-stretch my-auto bg-transparent outline-none appearance-none w-full pr-6"
                  value={entriesPerPage}
                  onChange={(e) => {
                    setEntriesPerPage(e.target.value)
                    setCurrentPage(1)
                  }}
                  aria-label="Entries per page"
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                  }}
                >
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex w-4 h-4">
                  <SmallArrowDownIcon />
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch min-w-60 text-sm font-normal grow shrink w-[356px] my-auto rounded-[0px_0px_0px_0px]">
            <div className="flex items-center gap-1 border border-[#F0F0F0] bg-white px-3 py-2 rounded-md w-full focus-within:border-[#DE3C7D]">
              <SearchIcon />
              <input
                type="text"
                placeholder="Search admin..."
                className="text-[#535862] bg-transparent outline-none border-none w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={() => setShowAddParentModal(true)}
            className="justify-center items-stretch self-stretch flex flex-col text-white text-center grow shrink w-[81px] bg-[#DE3C7D] my-auto p-2.5 rounded-md flex-shrink-0"
          >
            <div className="flex w-full gap-1 items-center justify-center">
              <div className="text-white">Add Parent</div>
              <div className="flex w-3.5 shrink-0 h-3.5">
                <AddIcon />
              </div>
            </div>
          </button>
        </div>
      </div>
      {/* End Header Controls Section */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0]">
                    S.No
                  </th>
                  <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                    Parent Name
                  </th>
                  <th className="text-sm text-[#535862] font-medium text-left border w-[280px] px-5 py-3.5 border-[#F0F0F0]">
                    Email Address
                  </th>
                  <th className="text-sm text-[#535862] font-medium text-center border px-5 py-3.5 border-[#F0F0F0]">
                    Status
                  </th>
                  <th className="text-sm text-[#535862] font-medium text-center px-5 py-3.5 border border-[#F0F0F0]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentEntries.map((item, index) => (
                  <tr key={item._id} className="border border-[#F0F0F0]">
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                      {String(indexOfFirstEntry + index + 1).padStart(2, '0')}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] font-semibold">
                      {`${item.first_name} ${item.last_name}`}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      {item.email}
                    </td>
                    <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                      <div className="flex justify-center">
                        <StatusToggle
                          initialStatus={item.status}
                          onChange={() =>
                            handleStatusToggle(item._id, item.status)
                          }
                        />
                      </div>
                    </td>
                    <td className="px-5 py-2">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => {
                            setSelectedParentId(item._id)
                            setShowAddStudentModal(true)
                          }}
                          className="border bg-neutral-50 p-2 rounded-[10px] border-[#F0F0F0] hover:bg-neutral-100 transition-colors"
                          aria-label="Add Student"
                        >
                          <span className="text-[#535862] font-bold text-lg">
                            +
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedParentId(item._id)
                            setShowEditParentModal(true)
                          }}
                          className="border bg-neutral-50 p-2 rounded-[10px] border-[#F0F0F0] hover:bg-neutral-100 transition-colors"
                          aria-label="Edit"
                        >
                          <span className="text-[#535862]">
                            <EditIcon />
                          </span>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item._id)}
                          className="border bg-[rgba(233,0,4,0.10)] p-2 rounded-[10px] border-[rgba(233,0,4,0.20)] hover:bg-[rgba(233,0,4,0.15)] transition-colors"
                          aria-label="Delete"
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-[#535862]">
                      No parents found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {data.length > 0 && (
              <div className="flex justify-between items-center mt-4 px-4">
                <div className="text-sm text-[#535862]">
                  Showing {indexOfFirstEntry + 1} to{' '}
                  {Math.min(indexOfLastEntry, data.length)} of {data.length}{' '}
                  entries
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`px-3 py-1 border border-[#F0F0F0] rounded-md ${
                        currentPage === index + 1
                          ? 'bg-[#DE3C7D] text-white'
                          : ''
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Parent Modal */}
      {showAddParentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[20px] w-[800px] max-h-[90vh] overflow-y-auto">
            <AddParent onClose={() => setShowAddParentModal(false)} />
          </div>
        </div>
      )}

      {/* Edit Parent Modal */}
      {showEditParentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[20px] w-[800px] max-h-[90vh] overflow-y-auto">
            <EditParent
              onClose={() => setShowEditParentModal(false)}
              parentId={selectedParentId}
            />
          </div>
        </div>
      )}

      {/* Add Student Modal */}
      {showAddStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[20px] w-[800px] max-h-[90vh] overflow-y-auto">
            <AddStudent
              onClose={() => setShowAddStudentModal(false)}
              parentId={selectedParentId}
            />
          </div>
        </div>
      )}

      {/* Status Toggle Confirmation Modal */}
      <ConfirmModal
        open={statusModalOpen}
        title={
          statusModalAction?.type === 'block'
            ? 'Deactivate Parent'
            : 'Activate Parent'
        }
        message={
          statusModalAction?.type === 'block'
            ? 'Are you sure you want to deactivate this parent?'
            : 'Are you sure you want to activate this parent?'
        }
        onCancel={handleStatusModalCancel}
        onConfirm={handleStatusModalConfirm}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        title="Delete Parent"
        message="Are you sure you want to delete this parent? This action cannot be undone."
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      <ToastContainer />
    </section>
  )
}

export default ParentList
